import { useEffect } from 'react';

import { t } from '@web/i18n';
import { PageSectionHead, Savebar } from '@web/ui/core';
import { SegmentedButton } from '@web/ui/core/organisms';

import { BoardGroup } from '../components/board_group';
import { useOrgBoardConfigPage } from '../hooks/use_org_board_config_page';

export default function OrgBoardConfigPage(): React.JSX.Element | null {
  const ctrl = useOrgBoardConfigPage();

  // ⌘S / Ctrl+S to save when dirty, Esc to discard. DOM subscription
  // is the canonical use case for useEffect per apps/clients/web/AGENTS.md.
  useEffect(() => {
    if (!ctrl.isReady) return undefined;
    function onKey(e: KeyboardEvent): void {
      if ((e.metaKey || e.ctrlKey) && e.key === 's' && ctrl.isDirty) {
        e.preventDefault();
        ctrl.save();
      } else if (e.key === 'Escape' && ctrl.isDirty) {
        ctrl.discard();
      }
    }
    globalThis.addEventListener('keydown', onKey);
    return () => {
      globalThis.removeEventListener('keydown', onKey);
    };
  }, [ctrl]);

  if (!ctrl.isReady) return null;

  return (
    <>
      <PageSectionHead title={t('orgBoardConfig.title')} sub={t('orgBoardConfig.sub')} />

      <BoardGroup
        title={t('orgBoardConfig.section.cardColumns')}
        sub={t('orgBoardConfig.section.cardColumns.sub')}
        cols={ctrl.cardCols}
      >
        <div className="flex flex-wrap items-center justify-between gap-3.5">
          <div className="flex flex-col gap-0.5">
            <span className="text-card-meta font-medium text-foreground">
              {t('orgBoardConfig.retention.label')}
            </span>
            <span className="text-meta text-muted-foreground">
              {t('orgBoardConfig.retention.hint')}
            </span>
          </div>
          <SegmentedButton ariaLabel={t('orgBoardConfig.retention.aria')}>
            {ctrl.retentionOptions.map((opt) => (
              <SegmentedButton.Item
                key={opt}
                label={opt}
                isActive={ctrl.retention === opt}
                onClick={() => {
                  ctrl.setRetention(opt);
                }}
              />
            ))}
          </SegmentedButton>
        </div>
      </BoardGroup>

      <BoardGroup
        title={t('orgBoardConfig.section.taskColumns')}
        sub={t('orgBoardConfig.section.taskColumns.sub')}
        cols={ctrl.taskCols}
      />

      <Savebar
        open={ctrl.isDirty}
        disabled={ctrl.isSaving}
        onDiscard={ctrl.discard}
        onSave={ctrl.save}
      />
    </>
  );
}
